import { useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useResendVerificationMutation } from '../../store/trialApi';
import { useTrialEmailVerification } from '../../hooks/useTrialEmailVerification';
import PageTitle from '../../components/shared/PageTitle';

export default function TrialVerifyEmailPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: isAuthLoading, authError } = useAuth();

  const email = (location.state as { email?: string })?.email ?? '';
  const tokenFromUrl = searchParams.get('token');

  const { state: verifyState, error: verifyError } = useTrialEmailVerification(tokenFromUrl);
  const [resendVerification, { isLoading: isResending }] = useResendVerificationMutation();
  const [resentSuccess, setResentSuccess] = useState(false);

  const handleResend = async () => {
    if (!email) return;
    setResentSuccess(false);
    try {
      await resendVerification({ email }).unwrap();
      setResentSuccess(true);
    } catch {
      setResentSuccess(true); // 200 regardless to prevent enumeration
    }
  };

  if (verifyState === 'awaiting-session' && isAuthenticated && !isAuthLoading) {
    return <Navigate to="/dashboard" replace />;
  }

  // The token verified and signInWithCustomToken succeeded, but the session
  // never finished resolving (e.g. /vendors/me failed) — stop waiting instead
  // of spinning forever; fall through to the normal error display below.
  const stuckAwaitingSession = verifyState === 'awaiting-session' && !!authError;

  if ((verifyState === 'verifying' || verifyState === 'awaiting-session') && !stuckAwaitingSession) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 text-sm">Verifying your email…</p>
        </div>
      </div>
    );
  }

  const displayError = stuckAwaitingSession ? authError : verifyError;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
      <PageTitle title={t('verify.pageTitle')} />

      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="https://clareefai.com">
            <img src="/logo-color.png" alt="Clareefai" className="h-8 w-auto mx-auto" />
          </a>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-violet-50 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('verify.title')}</h1>
          <p className="text-slate-500 mb-1">{t('verify.subtitle')}</p>
          {email && <p className="text-violet-600 font-medium mb-4">{email}</p>}
          <p className="text-sm text-slate-400 mb-8">{t('verify.instruction')}</p>

          {displayError && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-6 text-left">
              {displayError}
            </div>
          )}

          {resentSuccess ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 mb-6">
              {t('verify.resentSuccess')}
            </div>
          ) : (
            <button
              onClick={handleResend}
              disabled={isResending || !email}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl border border-gray-200 bg-white text-slate-600 hover:border-gray-300 hover:bg-gray-50 text-sm font-medium transition-colors disabled:opacity-50 mb-4"
            >
              {isResending ? (
                <>
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {t('verify.resending')}
                </>
              ) : (
                t('verify.resend')
              )}
            </button>
          )}

          <div className="space-y-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-slate-400">{t('verify.checkSpam')}</p>
            <p className="text-xs text-slate-400">
              {t('verify.wrongEmail')}{' '}
              <Link
                to="/trial/signup"
                className="text-violet-600 hover:text-violet-700 font-medium transition-colors"
              >
                {t('verify.startOver')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
